import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Send, Mail, MessageSquare, FileText, BarChart3,
  Loader2, CheckCircle, XCircle, Clock, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const MarketingConsole = () => {
  const { token, getAuthHeaders, isCalendarAdmin } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isCalendarAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isCalendarAdmin, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/marketing/logs?limit=100`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_BASE_URL}/marketing/stats`, {
          headers: getAuthHeaders()
        })
      ]);
      
      if (logsRes.data.success) setLogs(logsRes.data.data);
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (err) {
      setError('Failed to load marketing data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <button
            onClick={() => navigate('/marketing')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketing
          </button>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Marketing Console</h1>
              <p className="text-gray-600 mt-2">Track your marketing campaign performance</p>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Sent</p>
                    <p className="text-3xl font-bold text-green-600">{stats.total_sent || 0}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-green-200" />
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Failed</p>
                    <p className="text-3xl font-bold text-red-600">{stats.total_failed || 0}</p>
                  </div>
                  <XCircle className="w-10 h-10 text-red-200" />
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active Campaigns</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.active_campaigns || 0}</p>
                  </div>
                  <Clock className="w-10 h-10 text-blue-200" />
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Templates</p>
                    <p className="text-3xl font-bold text-yellow-600">{stats.templates_count || 0}</p>
                  </div>
                  <FileText className="w-10 h-10 text-yellow-200" />
                </div>
              </div>
            </div>
          )}

          {/* Send History Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Send History
              </h2>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
              </div>
            ) : logs.length === 0 ? (
              <div className="p-12 text-center">
                <Send className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Send History</h3>
                <p className="text-gray-500">Campaign send logs will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Channel</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.map((log, index) => (
                      <tr key={log.id || index} data-testid={`log-${log.id || index}`}>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(log.sent_at)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="flex items-center text-sm">
                            {log.channel === 'sms' ? (
                              <MessageSquare className="w-4 h-4 mr-1 text-blue-500" />
                            ) : log.channel === 'both' ? (
                              <>
                                <Mail className="w-4 h-4 mr-1 text-yellow-500" />
                                <MessageSquare className="w-4 h-4 mr-1 text-blue-500" />
                              </>
                            ) : (
                              <Mail className="w-4 h-4 mr-1 text-yellow-500" />
                            )}
                            {log.channel || 'email'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {log.total_recipients || 0}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-green-600 font-medium">
                            {log.sent_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm font-medium ${log.failed_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {log.failed_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {log.failed_count === 0 ? (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              Success
                            </span>
                          ) : log.sent_count > 0 ? (
                            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                              Partial
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                              Failed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default MarketingConsole;
